/* ==========================================================================
   HAPŪ HELPERS — Article enhancer
   --------------------------------------------------------------------------
   1. Builds TOC from H2s, smooth-scrolls on click.
   2. IntersectionObserver tracks active heading, highlights TOC link.
   3. On mobile, active heading title also replaces "In this article"
      label in the sticky dropdown summary.
   4. Mobile <details> closes after link selection.
   5. Share buttons: Web Share API on mobile, explicit buttons on desktop.
   ========================================================================== */

(function () {
  'use strict';

  var TOC_MEDIA_QUERY = '(max-width: 980px)';
  var mql = window.matchMedia(TOC_MEDIA_QUERY);

  // Extra clearance when scrolling to a heading on mobile.
  // Accounts for the sticky TOC bar (~6rem incl. padding) plus 2rem breathing.
  var MOBILE_EXTRA_OFFSET = 80; // px ≈ 6rem TOC + 2rem buffer

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    buildTOC();
    setupShare();
  }

  /* ====================== TOC ====================== */

  function buildTOC() {
    var tocEl = document.querySelector('[data-article-toc]');
    var listEl = document.querySelector('[data-article-toc-list]');
    var bodyEl = document.querySelector('[data-article-body]');
    if (!tocEl || !listEl || !bodyEl) return;

    var content = bodyEl.querySelector('.hh-article__content') || bodyEl;
    var headings = content.querySelectorAll('h2');
    if (headings.length < 2) return;

    var usedIds = new Set();
    var tocLinks = [];
    var DEFAULT_LABEL = 'In this article';

    // Capture the label element so we can swap its text on mobile
    var labelEl = tocEl.querySelector('.hh-article__toc-label');

    headings.forEach(function (h2) {
      var id = h2.id || slugify(h2.textContent || '');
      if (!id) return;
      var unique = id;
      var n = 2;
      while (usedIds.has(unique)) {
        unique = id + '-' + n;
        n++;
      }
      h2.id = unique;
      usedIds.add(unique);

      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + unique;
      a.textContent = (h2.textContent || '').trim();
      a.dataset.tocFor = unique;
      a.addEventListener('click', function (e) {
        e.preventDefault();
        // Close the dropdown first on mobile, then scroll
        if (mql.matches) {
          tocEl.open = false;
        }
        scrollToHeading(h2);
        history.replaceState(null, '', '#' + unique);
      });
      li.appendChild(a);
      listEl.appendChild(li);
      tocLinks.push({ link: a, heading: h2 });
    });

    tocEl.hidden = false;
    setupOpenStateForViewport(tocEl);

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', function () {
        setupOpenStateForViewport(tocEl);
        updateLabel();
      });
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(function () {
        setupOpenStateForViewport(tocEl);
        updateLabel();
      });
    }

    observeHeadings(tocLinks, updateLabel);

    if (window.location.hash) {
      var target = document.getElementById(window.location.hash.slice(1));
      if (target) setTimeout(function () { scrollToHeading(target); }, 100);
    }

    /* --- Label swap logic ---
       On mobile, when an active heading exists, replace the "In this article"
       label with the active heading's text. On desktop, always show default.
    */
    function updateLabel() {
      if (!labelEl) return;
      if (!mql.matches) {
        labelEl.textContent = DEFAULT_LABEL;
        return;
      }
      var active = listEl.querySelector('a.is-active');
      if (active) {
        labelEl.textContent = active.textContent;
      } else {
        labelEl.textContent = DEFAULT_LABEL;
      }
    }
  }

  function setupOpenStateForViewport(tocEl) {
    if (mql.matches) {
      tocEl.open = false;
    } else {
      tocEl.open = true;
    }
  }

  function observeHeadings(tocLinks, onActiveChange) {
    if (!('IntersectionObserver' in window)) return;

    var visible = new Map();

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var id = entry.target.id;
        if (entry.isIntersecting) {
          visible.set(id, entry.boundingClientRect.top);
        } else {
          visible.delete(id);
        }
      });
      updateActive();
    }, {
      rootMargin: '0px 0px -50% 0px',
      threshold: 0
    });

    tocLinks.forEach(function (item) { observer.observe(item.heading); });

    function updateActive() {
      var activeId = null;

      if (visible.size > 0) {
        var bestTop = Infinity;
        visible.forEach(function (top, id) {
          if (top < bestTop) {
            bestTop = top;
            activeId = id;
          }
        });
      } else {
        var scrollY = window.pageYOffset;
        var headerOffset = getHeaderOffset();
        var threshold = scrollY + headerOffset + (window.innerHeight * 0.5);
        var lastPassed = null;
        tocLinks.forEach(function (item) {
          var top = item.heading.getBoundingClientRect().top + scrollY;
          if (top <= threshold) lastPassed = item.heading.id;
        });
        activeId = lastPassed;
      }

      tocLinks.forEach(function (item) {
        if (item.link.dataset.tocFor === activeId) {
          item.link.classList.add('is-active');
        } else {
          item.link.classList.remove('is-active');
        }
      });

      if (typeof onActiveChange === 'function') onActiveChange();
    }

    updateActive();
    var scrollTimer;
    window.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateActive, 80);
    }, { passive: true });
  }

  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  function scrollToHeading(el) {
    var headerOffset = getHeaderOffset();
    var extra = mql.matches ? MOBILE_EXTRA_OFFSET : 20;
    var top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset - extra;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function getHeaderOffset() {
    var computed = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var parsed = parseInt(computed, 10);
    return isNaN(parsed) ? 80 : parsed;
  }

  /* ====================== SHARE ====================== */

  function setupShare() {
    var nativeBtn = document.querySelector('[data-share-native]');
    var explicitWrap = document.querySelector('[data-share-explicit]');
    var copyBtn = document.querySelector('[data-share-copy]');
    var status = document.querySelector('[data-share-status]');

    if (nativeBtn && typeof navigator.share === 'function') {
      nativeBtn.hidden = false;
      if (explicitWrap) explicitWrap.hidden = true;
      nativeBtn.addEventListener('click', function () {
        var url = nativeBtn.dataset.shareUrl;
        var title = nativeBtn.dataset.shareTitle;
        navigator.share({ title: title, url: url }).catch(function () {});
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var url = copyBtn.dataset.shareUrl;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(
            function () { showStatus(status, 'Copied!'); },
            function () { fallbackCopy(url); showStatus(status, 'Copied!'); }
          );
        } else {
          fallbackCopy(url);
          showStatus(status, 'Copied!');
        }
      });
    }
  }

  function showStatus(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(function () {
      el.classList.remove('is-visible');
      el.textContent = '';
    }, 2000);
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }
})();